import { TokenResult } from "@stripe/stripe-js";
import { Input, Modal, roundNumber } from "@xola/ui-kit";
import { FormField, handleSuccessfulSubmission, InputField, requiredEmail, requiredString } from "common";
import { useFormikContext } from "formik";
import React, { Ref, useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { LineItemObject, LineItemType, PaymentLineItemStatus, PaymentMethod, Purchase, Traveler } from "types";
import { number, object } from "yup";
import { api } from "../../../api/api";
import { getFormValuesForAuthorizeNet, handlePaymentError } from "../../../helpers/authNetPaymentHelper";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { getCreditCardLineItem, shouldEnableDrawerEject } from "../../../models/Purchase";
import { isAuthorizeNetEnabled } from "../../../models/Seller";
import { getAutoPrint } from "../../../storages/PrinterStorage";
import { useCreatePurchaseStore } from "../../../stores/createPurchaseStore";
import { getSeller } from "../../../utils/getSeller";
import { Printer } from "../../Common/Printer";
import { XolaFormik } from "../../Common/XolaFormik";
import { PaymentInput } from "../../Input/PaymentInput";
import { PaymentInputRef, TokenizePaymentParams } from "../../Input/PaymentInputRef";
import { Loader } from "../../Loader";
import { PaymentBreakdown } from "../../PurchaseModification/PaymentBreakdown";
import {
    SplitPayDemographics,
    SplitPayFormValues,
    SplitPaymentInitialValues,
    SplitPayOptions,
} from "./CreatePurchaseSplitPayTypes";
import { CreateSplitPayPurchaseFooter } from "./CreateSplitPayPurchaseFooter";
import { SplitPayBreakdown } from "./SplitPayBreakdown";
import { SplitPayOptionFields } from "./SplitPayOptionFields";

interface CreatePurchaseSplitPaymentModalProps {
    isOpen: boolean;
    purchase: Purchase;
    onClose: () => void;
    travelerId?: ID;
}

export const CreatePurchaseSplitPaymentModal = ({
    isOpen,
    purchase,
    onClose,
    travelerId,
}: CreatePurchaseSplitPaymentModalProps) => {
    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <ModalContent purchase={purchase} travelerId={travelerId} onClose={onClose} />
        </Modal>
    );
};

export const enum SplitPayFormKeys {
    CUSTOMER_EMAIL = "customerEmail",
}

interface ModalContentProps {
    purchase: Purchase;
    onClose: () => void;
    travelerId?: ID;
}

const ModalContent = ({ purchase, onClose, travelerId }: ModalContentProps) => {
    const paymentInputRef = useRef<PaymentInputRef>(null);
    const invalidate = useInvalidate();
    const seller = useSeller();
    const [enableDrawerEject, setEnableDrawerEject] = useState<boolean>(false);

    const splitPaymentInitialValues: SplitPaymentInitialValues = {
        demographics: [],
        addons: [],
        taxes: 0,
    };

    const splitPayDemographicsInitialValues = purchase.items.map((item) => {
        return { id: item.id, splitPayItemValues: { ...splitPaymentInitialValues } };
    });
    const [splitPayDemographics, setSplitPayDemographics] = useState<SplitPayDemographics>({
        items: splitPayDemographicsInitialValues,
    });

    const { data: traveler, isLoading: isFetchingTraveler } = useResource<Traveler>(["users", travelerId], {
        private: true,
    });

    const initialValues: SplitPayFormValues = {
        customerName: traveler?.name ?? "",
        customerEmail: traveler?.email ?? "",
        phone: traveler?.phone ?? "",
        amount: 0,
        notify: true,
        divideParts: 0,
        payParts: 0,
        splitPayOption: SplitPayOptions.CUSTOM_AMOUNT,
        payment: { method: PaymentMethod.CREDIT_CARD },
        isPayAndAddOption: true,
        chargeCard: false,
    };

    const emptyFormValues = (setValues: any) => {
        setValues({
            customerName: "",
            customerEmail: "",
            phone: "",
            amount: 0,
            notify: true,
            divideParts: 0,
            payParts: 0,
            splitPayOption: SplitPayOptions.CUSTOM_AMOUNT,
            payment: { method: PaymentMethod.CREDIT_CARD },
            isPayAndAddOption: true,
            chargeCard: false,
        });
        setSplitPayDemographics({ items: splitPayDemographicsInitialValues });
    };

    const handleSubmit = async (values: SplitPayFormValues, setValues: any) => {
        try {
            if (values.isPayAndAddOption) {
                const updatedPurchase = await collectCustomAmount(values);
                handleSuccessfulSubmission(() => {
                    handlePrint(updatedPurchase);
                    emptyFormValues(setValues);
                    invalidate("purchases", purchase.id);
                });
            } else {
                const updatedPurchase = await collectRemainingBalance(values);
                handleSuccessfulSubmission(() => {
                    handlePrint(updatedPurchase);
                    onClose();
                    invalidate("purchases", purchase.id);
                });
            }
        } catch (error: any) {
            // This is required to open additional billing details modal in case there is an error while making authorize.net payment
            handlePaymentError(error, seller, paymentInputRef);
        }
    };

    const handlePrint = (updatedPurchase?: Purchase) => {
        if (getAutoPrint() && updatedPurchase) {
            const drawerEjectPreference = shouldEnableDrawerEject(updatedPurchase);
            setEnableDrawerEject(drawerEjectPreference);
        }
    };

    const {
        mutateAsync: collectCustomAmount,
        isLoading: isSavingCustomAmount,
        isSuccess: customAmountSuccess,
    } = useMutation(async (values: SplitPayFormValues) => collectPurchaseBalance(values));

    const {
        mutateAsync: collectRemainingBalance,
        isLoading: isSavingPurchaseBalance,
        isSuccess: purchaseBalanceSuccess,
    } = useMutation(async (values: SplitPayFormValues) => collectPurchaseBalance(values));

    const getFormValues = async (values: SplitPayFormValues, tokenizePaymentParams?: TokenizePaymentParams) => {
        if (isAuthorizeNetEnabled(seller)) {
            return getFormValuesForAuthorizeNet(values, paymentInputRef, tokenizePaymentParams);
        }

        const tokenResult: TokenResult | null = values.chargeCard
            ? ((await paymentInputRef.current?.tokenize()) as TokenResult | null) ?? null
            : null;

        if (tokenResult?.token?.id) {
            const payment = {
                token: { id: tokenResult?.token?.id },
                method: PaymentMethod.CREDIT_CARD,
            };
            return {
                ...values,
                payment: payment,
            };
        }

        return values;
    };

    const collectPurchaseBalance = async (values: SplitPayFormValues) => {
        if (purchase) {
            const formValues = (await getFormValues(values)) as SplitPayFormValues;

            const { data: updatedPurchase } = await collectBalance(formValues);

            const creditCardPaymentLineItem = getCreditCardLineItem(updatedPurchase);

            const clientSecret = creditCardPaymentLineItem?.payment?.paymentIntent?.clientSecret;

            if (clientSecret) {
                await paymentInputRef.current?.confirm?.({ clientSecret });

                await api.patch(`/purchases/${purchase.id}/lineItems/${creditCardPaymentLineItem.id}`, {
                    status: PaymentLineItemStatus.PROCESSING,
                });

                paymentInputRef.current?.closeEMVModal?.();
            }

            return updatedPurchase;
        }
    };

    const collectBalance = async (values: SplitPayFormValues) => {
        const payload = getPayload(values);
        return api.patch<Purchase>(`/purchases/${purchase?.id}`, payload);
    };

    const getPayload = (values: SplitPayFormValues) => {
        const seller = getSeller();
        return {
            ops: [
                {
                    path: "/lineItems",
                    op: "addLineItem",
                    data: {
                        amount: -1 * roundNumber(purchase.currency ?? seller.currency, values.amount),
                        name: "Payment",
                        object: LineItemObject.PAYMENT,
                        type: LineItemType.PAYMENT,
                        payment: values.payment,
                        status: values.chargeCard ? PaymentLineItemStatus.PENDING : PaymentLineItemStatus.PROCESSING,
                        _emailOverrides: { sendEmailToTraveler: values.notify },
                        traveler: {
                            name: values.customerName,
                            email: values.customerEmail,
                            phone: values.phone,
                        },
                    },
                },
            ],
        };
    };

    const handleAdditionalBillingSubmit = async (
        tokenizePaymentParams: TokenizePaymentParams,
        values: SplitPayFormValues,
    ) => {
        try {
            const formValues = (await getFormValues(values, tokenizePaymentParams)) as SplitPayFormValues;
            await collectBalance(formValues);
            handleSuccessfulSubmission(() => {
                onClose();
                invalidate("purchases", purchase.id);
            });
        } catch (error: any) {
            paymentInputRef.current?.onPaymentError?.(error.response?.data?.global);
        }
    };

    const savedCards = traveler?.creditCards ?? [];
    const [hasOrganizerChanged, setHasOrganizerChanged] = useState(false);

    const handlePrintFinish = () => {
        // setting this to false as there is a possibility of multiple payments in make a purchase split pay
        setEnableDrawerEject(false);
    };

    if (isFetchingTraveler) {
        return <Loader />;
    }

    return (
        <XolaFormik
            enableReinitialize
            enablePrompt={false}
            validateOnChange={false}
            initialValues={initialValues}
            validationSchema={getValidationSchema(purchase)}
            onSubmit={async (values: SplitPayFormValues, { setValues }) => handleSubmit(values, setValues)}
        >
            {({ values, setFieldValue }) => {
                const handleCustomerChange = (e: any) => {
                    const updatedValue = e.target.value;
                    const initialValue = initialValues[SplitPayFormKeys.CUSTOMER_EMAIL];
                    setFieldValue(SplitPayFormKeys.CUSTOMER_EMAIL, updatedValue);
                    if (updatedValue !== initialValue) {
                        // remove card
                        setFieldValue("payment.card", undefined);
                        setHasOrganizerChanged(true);
                    } else if (updatedValue === initialValue && savedCards.length > 0) {
                        // set card back to initial value
                        setFieldValue("payment.card", savedCards?.[0]?.id);
                        setHasOrganizerChanged(false);
                    }
                };

                return (
                    <>
                        <Modal.Header>Split Payment</Modal.Header>
                        <Modal.Body className="grid grid-cols-2 gap-8">
                            <div>
                                <FormField name="customerName" label="Name">
                                    <InputField />
                                </FormField>

                                <FormField name="customerEmail" label="Email">
                                    <Input
                                        data-testid="split-payment-customer-email"
                                        value={values.customerEmail}
                                        onChange={(e: any) => handleCustomerChange(e)}
                                    />
                                </FormField>

                                <FormField name="phone" label="Phone">
                                    <InputField />
                                </FormField>

                                <SplitPayOptionFields
                                    purchase={purchase}
                                    splitPayDemographics={splitPayDemographics}
                                    onSplitPayDemographicsChange={(splitPayDemographics: SplitPayDemographics) => {
                                        setSplitPayDemographics(splitPayDemographics);
                                    }}
                                />

                                <hr className="text-gray-light" />

                                <SplitPayPayment
                                    paymentInputRef={paymentInputRef}
                                    traveler={hasOrganizerChanged ? undefined : traveler}
                                    onAdditionalDetailsSubmit={async (data) =>
                                        handleAdditionalBillingSubmit(data, values)
                                    }
                                />
                            </div>

                            <div className="bg-gray-lighter p-6">
                                {purchase && purchase.items.length > 0 && (
                                    <PaymentBreakdown purchase={purchase} payload={getPayload(values)} />
                                )}
                                {values.splitPayOption === SplitPayOptions.SELECT_DEMOGRAPHIC_AND_ADDON &&
                                    splitPayDemographics && (
                                        <SplitPayBreakdown
                                            splitPayDemographics={splitPayDemographics}
                                            purchase={purchase}
                                        />
                                    )}
                            </div>
                            <Printer
                                enablePaperCut
                                enableDrawerEject={enableDrawerEject}
                                shouldStart={enableDrawerEject}
                                onFinish={handlePrintFinish}
                            />
                        </Modal.Body>
                        <Modal.Footer className="flex justify-end">
                            <CreateSplitPayPurchaseFooter
                                isSavingCustomAmount={isSavingCustomAmount}
                                isSavingPurchaseBalance={isSavingPurchaseBalance}
                                customAmountSuccess={customAmountSuccess}
                                purchaseBalanceSuccess={purchaseBalanceSuccess}
                                onClose={onClose}
                            />
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

interface SplitPayPaymentProps {
    paymentInputRef: Ref<PaymentInputRef>;
    traveler?: Traveler;
    onAdditionalDetailsSubmit: (tokenizePaymentParams: TokenizePaymentParams) => Promise<void>;
}

const SplitPayPayment = ({ paymentInputRef, traveler, onAdditionalDetailsSubmit }: SplitPayPaymentProps) => {
    const { values, setFieldValue, setFieldError } = useFormikContext<SplitPayFormValues>();
    const isManual = useCreatePurchaseStore((state) => !state.isEMV);

    const handleChargeCard = (value: boolean) => {
        if (values.payment.method === PaymentMethod.CREDIT_CARD) {
            setFieldValue("chargeCard", value);
        }
    };

    useEffect(() => {
        if (isManual && values.payment.paymentIntentMethods?.includes("card_present")) {
            delete values.payment.paymentIntentMethods;
            setFieldValue("payment", values.payment);
        }

        if (!isManual && !values.payment.paymentIntentMethods?.length) {
            delete values.payment.card;
            setFieldValue("payment", { ...values.payment, paymentIntentMethods: ["card_present"] });
        }
    }, [isManual, values.payment, setFieldValue]);

    return (
        <div className="space-y-4">
            <PaymentInput
                ref={paymentInputRef}
                value={values.payment}
                organizer={traveler?.id}
                isChargeCard={values.chargeCard}
                excludePaymentMethods={[PaymentMethod.INVOICE]}
                onChange={(value) => setFieldValue("payment", value)}
                onChargeCard={(value) => handleChargeCard(value)}
                onError={(error) => setFieldError("payment", error ?? undefined)}
                onAdditionalDetailsSubmit={onAdditionalDetailsSubmit}
            />
        </div>
    );
};

const getValidationSchema = (purchase: Purchase) => {
    return object({
        customerName: requiredString(),
        customerEmail: requiredEmail(),
        amount: number()
            .nullable()
            .when("splitPayOption", {
                is: (splitPayOption: SplitPayOptions) => splitPayOption === SplitPayOptions.CUSTOM_AMOUNT,
                then: number()
                    .nullable()
                    .required()
                    .positive()
                    .max(purchase.balance, `must be less than ${purchase.balance}`),
            }),
    });
};
